import { parse } from "date-fns";
import { useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import {
  selectedDateAtom,
  currentCalendarMonthDateAtom,
  filterPricingGuidAtom,
  isGroupPricingPackagesAtom,
  listingGuidAtom,
  campaignCodeAtom,
  campaignValidationAtom,
  trackingParamsAtom,
} from "./store";
import { useCampaignVisitMutation } from "../../api/campaign-hooks";
import { AddCampaignVisitCommand } from "@repo/rq-api-public";

export default function useSyncQueryParams(setStep: (step: 1 | 2 | 3) => void) {
  const hasInitialized = useRef(false);

  const setSelectedDate = useSetAtom(selectedDateAtom);
  const setCurrentCalendarMonth = useSetAtom(currentCalendarMonthDateAtom);
  const setFilterPricingGuid = useSetAtom(filterPricingGuidAtom);
  const setIsGroupPricingPackages = useSetAtom(isGroupPricingPackagesAtom);
  const setListingGuid = useSetAtom(listingGuidAtom);
  const setCampaignCode = useSetAtom(campaignCodeAtom);
  const setCampaignValidation = useSetAtom(campaignValidationAtom);
  const setTrackingParams = useSetAtom(trackingParamsAtom);

  const campaignVisitMutation = useCampaignVisitMutation();

  useEffect(() => {
    // Prevent running the effect multiple times using useRef
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    setStep(1);

    const searchParams = new URLSearchParams(window.location.search);

    const listingGuid = searchParams.get("listingGuid");
    if (!listingGuid) {
      throw new Error("Listing guid is required");
    }
    setListingGuid(listingGuid);

    const selectedDate = searchParams.get("date");
    const pricingGuid = searchParams.get("pricingGuid");
    const isGroupPricingPackages = searchParams.get("groupPricingPackages");
    const campaignCode = searchParams.get("campaignCode");

    setTrackingParams({
      fbp: searchParams.get("fbp"),
      fbc: searchParams.get("fbc"),
      gaClientId: searchParams.get("gaClientId"),
      gclid: searchParams.get("gclid"),
      marketplaceSessionId: searchParams.get("marketplaceSessionId"),
    });

    // Set campaign code if present
    if (campaignCode) {
      setCampaignCode(campaignCode);

      // Reset campaign validation state for new campaign code
      setCampaignValidation({
        isValid: null,
        isLoading: false,
      });

      // Call campaign visit API - this will only run once due to the useRef guard above
      const visitCommand = new AddCampaignVisitCommand({
        campaignCode: campaignCode,
      });
      campaignVisitMutation.mutate(visitCommand);
    }

    // comes in format 2024-06-19
    const parsedDate = selectedDate
      ? parse(selectedDate, "yyyy-MM-dd", new Date())
      : null;

    if (parsedDate) {
      setSelectedDate(parsedDate);
      setCurrentCalendarMonth(parsedDate);
    }

    if (pricingGuid) {
      // If pricing guid is set, we need to set the filter and group pricing packages
      setFilterPricingGuid(pricingGuid);
      setIsGroupPricingPackages(true);
    }

    if (!pricingGuid) {
      setIsGroupPricingPackages(isGroupPricingPackages === "true");
    }
  }, [
    setStep,
    setListingGuid,
    setCampaignCode,
    setCampaignValidation,
    campaignVisitMutation,
    setSelectedDate,
    setCurrentCalendarMonth,
    setFilterPricingGuid,
    setIsGroupPricingPackages,
    setTrackingParams,
  ]);
}
